import { createContext, useState, useEffect, useRef } from 'react'
import { getSessionById } from '../../../service/sessionService'
import { getGuiSessionConfig, isGuiSession } from '../../workspace/config/guiSessionConfig'
export const SessionDataContext = createContext()
export const SessionDataProvider = ({ children, sessionId }) => {
  const [session, setSession] = useState(null)
  const [sessionData, setSessionData] = useState({
    taskDescription: `You are given a list of song names called playlist.
Complete the following steps:
1.) The song at index 4 was added by mistake. Replace it with "Purple Rain".
2.) Remove the last 3 songs from the playlist using slicing.
3.) Reverse the playlist in place.
4.) Print every other song in the playlist using slicing with a step value.`,
    totalStudents: 36,
    finishedStudents: 22,
    passRate: 67,
    aiUsagePercent: 78,
    errorDistribution: [
      { time: '0m', syntax: 0, runtime: 0, logic: 0, test_failed: 0 },
      { time: '1m', syntax: 4, runtime: 1, logic: 0, test_failed: 0 },
      { time: '2m', syntax: 8, runtime: 2, logic: 0, test_failed: 1 },
      { time: '3m', syntax: 12, runtime: 4, logic: 1, test_failed: 2 },
      { time: '4m', syntax: 9, runtime: 6, logic: 2, test_failed: 4 },
      { time: '5m', syntax: 6, runtime: 7, logic: 3, test_failed: 6 },
      { time: '6m', syntax: 4, runtime: 5, logic: 6, test_failed: 8 },
      { time: '7m', syntax: 3, runtime: 3, logic: 8, test_failed: 5 },
      { time: '8m', syntax: 2, runtime: 2, logic: 6, test_failed: 3 },
      { time: '9m', syntax: 1, runtime: 1, logic: 4, test_failed: 2 },
      { time: '10m', syntax: 0, runtime: 0, logic: 2, test_failed: 1 },
    ],
    latestConversations: {
      totalConversations: 142,
      uniqueConversations: 24,
      recentMessages: [
        { content: "don't know the basic knowledge", senderId: 2, timestamp: new Date().toISOString() },
        { content: 'unproductive discussion (non relevant topic being discussed)', senderId: 3, timestamp: new Date().toISOString() },
        { content: 'asking about list indexing syntax', senderId: 4, timestamp: new Date().toISOString() },
        { content: 'struggling with nested for loop logic', senderId: 5, timestamp: new Date().toISOString() },
        { content: 'struggling with nested for loop logic', senderId: 5, timestamp: new Date().toISOString() },
      ]
    }
  })
  const [loading, setLoading] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const timerRef = useRef(null)

  // The rest of sessionData below is still placeholder/demo content (not wired to a real
  // fetch) - only taskDescription is replaced here, since that's the field TaskDescription.jsx
  // renders directly and instructors were seeing an unrelated hardcoded example for every session.
  // Polls rather than fetching once, so SessionLobby's server-seeded timer state
  // self-corrects within ~10s of a discrepancy - e.g. if an instructor's Start click's write
  // failed (or landed against a stale backend deploy) and the UI optimistically showed
  // "Active" without the server actually recording it, the next poll here brings `session`
  // back to true server state and SessionLobby's seed effect (keyed on `session`) re-derives
  // from it. See the incident this fixed: pressing Start appeared to work, but switching to
  // another Monitor sub-tab and back showed it reset to Inactive - the fix isn't the polling
  // alone (that surfaces the mismatch, it doesn't prevent it) - see SessionLobby.jsx's
  // handlers, which now check the write's own success/failure instead of always trusting it.
  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    const fetchSession = () => {
      getSessionById(sessionId).then((fetchedSession) => {
        if (cancelled || !fetchedSession) return
        setSession(fetchedSession)
        const guiConfig = getGuiSessionConfig(fetchedSession)
        const taskDescription = fetchedSession.task || guiConfig?.assignment
        if (taskDescription) {
          setSessionData((prev) => ({ ...prev, taskDescription }))
        }
      }).catch(() => {})
    }
    fetchSession()
    const poll = setInterval(fetchSession, 10000)
    return () => { cancelled = true; clearInterval(poll) }
  }, [sessionId])

  useEffect(() => {
    if (isActive) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1)
      }, 1000)
    } else {
      clearInterval(timerRef.current)
    }
    return () => clearInterval(timerRef.current)
  }, [isActive])
  const startSession = () => setIsActive(true)
  const stopSession = () => setIsActive(false)
  const resetSession = () => {
    setIsActive(false)
    setElapsedSeconds(0)
  }
  return (
    <SessionDataContext.Provider value={{
      sessionId,
      session,
      isGui: session ? isGuiSession(session) : false,
      sessionData,
      loading,
      isActive,
      elapsedSeconds,
      startSession,
      stopSession,
      resetSession,
    }}>
      {children}
    </SessionDataContext.Provider>
  )
}